import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.mjs";
import pdfWorker from "pdfjs-dist/legacy/build/pdf.worker.min.mjs?url";
import { markRaw, onBeforeUnmount, ref, shallowRef, unref, watch, type Ref } from "vue";
import { getMinioSignedUrl, invalidateMinioSignedUrl } from "@/functions/useMinioSignedUrl";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

export { pdfjsLib };

type PdfLoadingTask = ReturnType<typeof pdfjsLib.getDocument>;

function isPdfJsCancellation(error: unknown) {
  const name = (error as { name?: string })?.name;
  const message = error instanceof Error ? error.message : String(error ?? "");

  return (
    name === "AbortException" ||
    name === "RenderingCancelledException" ||
    message === "Loading aborted" ||
    message === "Worker was destroyed" ||
    message === "Worker was terminated" ||
    message === "Page was destroyed."
  );
}

function isRetryableAccessError(error: unknown) {
  const status = (error as { status?: unknown })?.status;
  if (status === 403) {
    return true;
  }

  const message = error instanceof Error ? error.message : String(error ?? "");
  return /server response \(403\)/i.test(message);
}

async function destroyLoadingTask(task: PdfLoadingTask | null) {
  try {
    await task?.destroy();
  } catch (error: unknown) {
    if (!isPdfJsCancellation(error)) {
      throw error;
    }
  }
}

export function usePdfDocument(fileUUID: Ref<string | undefined | null>) {
  const pdfLoading = ref(false);
  const pdfError = ref<unknown>();
  const pageCount = ref(0);
  const pdfDoc = shallowRef<any | null>(null);

  let loadingTask: PdfLoadingTask | null = null;
  let token = 0;

  async function load() {
    const currentFileUUID = unref(fileUUID);
    const currentToken = ++token;

    pdfLoading.value = true;
    pdfError.value = undefined;
    pageCount.value = 0;
    pdfDoc.value = null;

    try {
      const previousLoadingTask = loadingTask;
      loadingTask = null;
      await destroyLoadingTask(previousLoadingTask);

      if (!currentFileUUID) {
        return;
      }

      for (let attempt = 0; attempt < 2; attempt++) {
        if (attempt > 0) {
          invalidateMinioSignedUrl(currentFileUUID);
        }

        const currentUrl = await getMinioSignedUrl(currentFileUUID);
        if (currentToken !== token || !currentUrl) {
          return;
        }

        const currentLoadingTask = pdfjsLib.getDocument({
          url: currentUrl,
          withCredentials: false,
        });
        loadingTask = currentLoadingTask;

        try {
          const pdf = await currentLoadingTask.promise;
          if (currentToken !== token) {
            await pdf.destroy();
            return;
          }

          pdfDoc.value = markRaw(pdf);
          pageCount.value = pdf.numPages;
          return;
        } catch (error: unknown) {
          if (isPdfJsCancellation(error) || currentToken !== token) {
            return;
          }

          if (attempt === 0 && isRetryableAccessError(error)) {
            await currentLoadingTask.destroy().catch(() => undefined);
            if (loadingTask === currentLoadingTask) {
              loadingTask = null;
            }
            continue;
          }

          pdfError.value = error;
          return;
        }
      }
    } catch (error: unknown) {
      if (!isPdfJsCancellation(error)) {
        pdfError.value = error;
      }
    } finally {
      if (currentToken === token) {
        pdfLoading.value = false;
      }
    }
  }

  watch(fileUUID, load, { immediate: true });

  onBeforeUnmount(() => {
    token++;
    void destroyLoadingTask(loadingTask).catch(() => undefined);
    loadingTask = null;
  });

  return {
    pdfLoading,
    pdfError,
    pageCount,
    pdfDoc,
    reload: load,
  };
}
